import Admin from './screens/admin/Admin';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import CommandPalette from './components/CommandPalette';
import { Spinner, Toast } from './components/ui';
import { connectEvents } from './lib/events';
import { qk } from './lib/keys';
import { useSession } from './lib/queries';
import { ui } from './lib/store';
import AuthScreen from './screens/Auth';
import Projects from './screens/Projects';
import Settings from './screens/settings/Settings';
import Wizard from './screens/Wizard';
import Workspace from './screens/workspace/Workspace';

export default function App() {
  const session = useSession();
  const qc = useQueryClient();
  const nav = useNavigate();
  const loc = useLocation();
  const state = session.data?.state;


  // One live event stream for the whole signed-in app.
  useEffect(() => {
    if (state !== 'ok') return;
    return connectEvents(qc);
  }, [state, qc]);

  useEffect(() => {
    const onUnauth = () => void qc.invalidateQueries({ queryKey: qk.session });
    window.addEventListener('harness:unauthorized', onUnauth);
    return () => window.removeEventListener('harness:unauthorized', onUnauth);
  }, [qc]);

  // ⌘/Ctrl+K toggles the palette from anywhere after login.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && state === 'ok') {
        e.preventDefault();
        ui.set({ palette: !ui.get().palette });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state]);

  useEffect(() => {
    if (state === 'ok' && ['/login', '/2fa', '/setup'].includes(loc.pathname)) nav('/', { replace: true });
  }, [state, loc.pathname, nav]);

  if (session.isLoading || !session.data)
    return (
      <div style={{ height: '100%', display: 'grid', placeItems: 'center' }}>
        {session.error ? <div className="err">Can’t reach the harness server. Retrying…</div> : <Spinner />}
      </div>
    );

  if (state !== 'ok') {
    return (
      <>
        <Routes>
          <Route path="/reset" element={<AuthScreen mode="reset" />} />
          <Route path="*" element={<AuthScreen mode={state === 'mfa' ? 'twofa' : session.data.needsSetup ? 'setup' : 'login'} />} />
        </Routes>
        <Toast />
      </>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<Projects />} />
        <Route path="/new" element={<Wizard />} />
        <Route path="/p/:slug" element={<Workspace />} />
        <Route path="/p/:slug/c/:convId" element={<Workspace />} />
        <Route path="/p/:slug/:mode" element={<Workspace />} />
        <Route path="/p/:slug/settings/:tab" element={<Settings />} />
        <Route path="/settings/:tab" element={<Settings />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/admin/:tab" element={<Admin />} />
        <Route path="/admin/:tab/:id" element={<Admin />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <CommandPalette />
      <Toast />
    </>
  );
}
